import { IMaskedTextBoxControl } from "Controls/Interfaces";
import { Control } from "Controls/Control";
import { ControlEventTypes } from "Core/Events/EventTypes";

/**
 * ## Masked TextBox
 *
 * Control for entering values protected by a mask.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of a MaskedTextBoxControl with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class MaskedTextBoxControl extends Control implements IMaskedTextBoxControl {
    private _kendoElement: kendo.ui.MaskedTextBox;

    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-maskedtextbox";

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        this.jQueryElement.on("focusout", (e) => this.trigger(ControlEventTypes.LostFocus));
        let options: kendo.ui.MaskedTextBoxOptions = {
            mask: this.controlDefinition.Mask,
            rules: {
                "L": /[A-Z]/,
                "l": /[a-z]/,
            },
            change: (e) => this.onChange(e)
        };
        this._kendoElement = new kendo.ui.MaskedTextBox(this.jQueryElement[0], options);
    }

    private onChange(e: kendo.ui.MaskedTextBoxChangeEvent): void {
        this.trigger(ControlEventTypes.Change);
    }

    /**
     * Get control value.
     * @since 4.0.0
     */
    public getValue(): string {
        return this._kendoElement.value();
    }

    /**
     * Set control value.
     * @param value Value to be set.
     * @since 4.0.0
     */
    public setValue(value: string): void {
        let oldValue = this.getValue();
        if (oldValue !== value) {
            this._kendoElement.value(value);
            this._kendoElement.trigger("change");
        }
    }

    /**
     * Enable the control.
     * @since 4.0.0
     */
    public enable(): void {
        this._kendoElement.enable(true);
    }

    /**
     * Disable the control.
     * @since 4.0.0
     */
    public disable(): void {
        this._kendoElement.enable(false);
    }

    /**
     * Returns true if the control is enabled.
     * @since 4.0.0
     */
    public isEnabled(): boolean {
        return !this._kendoElement.element.prop("disabled");
    }

    /**
     * Gets the outer element. This is either the HTMLElement or a wrapper added by the control.
     * @since 4.2.1
     * @hidden
     */
    public getOuterElement(): JQuery {
        return this._kendoElement.wrapper;
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        if (this._kendoElement != null) {
            this._kendoElement.destroy();
        }
        if (this.jQueryElement != null) {
            this.jQueryElement.off("focusout");
        }
        super.dispose();
    }
}
